import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import DateRangeFilter from './DateRangeFilter';
import {filterStyles} from '../shared/styles-filter';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';

/**
 * Set a field for form entry
 *
 * @param touched
 * @param error
 * @param inputProps
 * @param props
 * @return {*}
 */
const renderInput = ({meta: {touched, error} = {}, input: {...inputProps}, ...props}) => {
    return (
        <TextField
            errorText={touched && error}
            {...inputProps}
            {...props}
            fullWidth
        />
    );
};

/**
 * A form containing components with Date Range, credit card, and monetary amounts
 *
 */
class CreditCardTransactionReportsFilter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedAmount: null,
            selectedLastFour: null,
            selectedEndDate: null,
            selectedStartDate: null,
            selectedOffset: (props.selectedOffset) ? props.selectedOffset : null,
        };

        this.handleDateRangeFilterSelect = this.handleDateRangeFilterSelect.bind(this);
        this.handleLastFourUpdate = this.handleLastFourUpdate.bind(this);
        this.handleAmountUpdate = this.handleAmountUpdate.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.selectedOffset !== this.props.selectedOffset) {
            this.setState({
                selectedOffset: nextProps.selectedOffset
            });
        }
    }

    /**
     * Handle events from DateRangeFilter
     *
     * @param type
     * @param value
     */
    handleDateRangeFilterSelect = (type, value) => {
        switch (type) {
            case 'start_date':
                this.setState({selectedStartDate: value});
                break;
            case 'end_date':
                this.setState({selectedEndDate: value});
                break;
            case 'offset':
                if (value) {
                    this.setState({
                        selectedOffset: value
                    }, () => {
                        this.props.dispatch(change('filterForm', 'offset', value));
                    });
                }
                break;
            default:
                return
        }
    };

    /**
     * Handle update event for Last Four Digits
     *
     * @param event
     * @param index
     * @param value
     */
    handleLastFourUpdate(event, index, value) {
        let pattern = /^\d+$/;
        let valid = pattern.test(index);
        if (!valid) {
            event.preventDefault();
        } else {
            this.setState({
                selectedLastFour: index
            });
        }
    }

    handleLastFourValidation = (e) => {
        let error = null;
        if (!e || e < 0 || e > 9999 || e.length !== 4 || isNaN(e)) {
            error = 'Must be 4 digits.';
        }
        return error;
    };

    validateLastFour = [this.handleLastFourValidation];

    /**
     * Handle update event for Amount
     *
     * @param event
     * @param index
     * @param value
     * @param name
     */
    handleAmountUpdate(event, index, value, name) {
        let pattern = /^[0-9]+([.][0-9]?[0-9]?)?$/;
        let valid = pattern.test(index);
        if (!valid && index !== '') {
            event.preventDefault();
        } else {
            this.setState({
                selectedAmount: index
            }, () => {
            });
        }
    }

    handleAmountValidation = (e) => {
        let error = null;
        if (e && e !== '') {
            if (e < 0.01 || isNaN(e)) {
                error = 'Please enter a valid number.';
            }
        }
        return error;
    };

    validateAmount = [this.handleAmountValidation];

    handleSubmit() {
        if (typeof this.props.onSubmit === 'function') {
            this.props.onSubmit({
                lastFour: this.state.selectedLastFour,
                amount: this.state.selectedAmount,
                startDate: this.state.selectedStartDate,
                endDate: this.state.selectedEndDate,
                offset: this.state.selectedOffset
            });
        }
    }

    disableSubmit = () => {
        let lastFour = this.state.selectedLastFour;
        if (
            !this.state.selectedStartDate ||
            !this.state.selectedEndDate ||
            !this.state.selectedOffset ||
            !lastFour || lastFour.length !== 4
        ) {
            return true;
        }
        return false;
    }

    render() {
        const {
            selectedAmount,
            selectedLastFour
        } = this.state;

        const styles = {
            button: {display: 'inline-block', margin: '0.5em 0 0 0'},
            field: {display: 'block', maxWidth: '80%'},
        };

        const submitDisabled = this.disableSubmit();

        return (
            <div className="list-filter">
                {/* Report Filter (Start Date, End Date, Time Zone) */}
                <DateRangeFilter
                    onSelectStartDate={this.handleDateRangeFilterSelect}
                    onSelectEndDate={this.handleDateRangeFilterSelect}
                    onSelectOffset={this.handleDateRangeFilterSelect}
                />
                <form>
                    <div className="list-filter">
                        <Field name="lastFour"
                               type="text"
                               component={renderInput}
                               floatingLabelText="Enter Last 4 Digits"
                               size={4}
                               maxLength={4}
                               value={selectedLastFour}
                               validate={this.validateLastFour}
                               onChange={this.handleLastFourUpdate}
                               style={filterStyles.inlineTextSmall}
                        />
                        <Field name="amount"
                               type="text"
                               component={renderInput}
                               floatingLabelText="Enter $ Amount"
                               value={selectedAmount}
                               validate={this.validateAmount}
                               onChange={this.handleAmountUpdate}
                               style={filterStyles.inlineTextSmall}
                        />
                    </div>
                    <RaisedButton
                        primary
                        label="Submit"
                        onClick={this.handleSubmit}
                        disabled={submitDisabled}
                        style={styles.button}
                    />
                </form>
                <hr style={filterStyles.break}/>
            </div>
        );
    }
}

function mapStateToProps(state) {
    let filterForm = state.form['filterForm'];
    let timeZoneForm = state.form['timeZoneForm'];
    const defaultOffsetValue = (timeZoneForm && timeZoneForm.values && timeZoneForm.values['timeZoneDefault']) ? timeZoneForm.values['timeZoneDefault'] : null;
    if (filterForm) {
        const offsetValue = (filterForm.values && filterForm.values['offset']) ? filterForm.values['offset'].toString() : defaultOffsetValue;
        return {
            selectedOffset: offsetValue
        };
    }
    return {};
}

CreditCardTransactionReportsFilter.propTypes = {
    onSubmit: PropTypes.func
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
    })
);

export default enhance(CreditCardTransactionReportsFilter);



// WEBPACK FOOTER //
// ./src/Fuse/components/CreditCardTransactionReportsFilter.js